import { useEffect, useRef, useState } from 'react'
import type { LayoutChangeEvent } from 'react-native'
import { Easing, useAnimatedStyle, useSharedValue, withTiming, type WithTimingConfig } from 'react-native-reanimated'

import { useDimensions } from '@/src/general/hooks/useDimensions'
import { assignAnimatedValue } from '@/src/general/utils/assignAnimatedValue'

const timingConfiguration: WithTimingConfig = { duration: 300, easing: Easing.bezier(0.5, 0.01, 0, 1) }

export const useDonationBannerAnimation = (showingDonationBanner: boolean) => {
  const translateY = useSharedValue(-220)
  const heightY = useSharedValue(0)
  const layoutRef = useRef({ messageHeight: 220 })
  const [messageHeight, setMessageHeight] = useState(220)

  const { lessThan } = useDimensions()

  const animatedHeightY = useAnimatedStyle(() => ({
    height: heightY.value,
  }))

  const animatedY = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }))

  useEffect(() => {
    if (lessThan.md) return
    if (showingDonationBanner) {
      assignAnimatedValue(heightY, withTiming(messageHeight, timingConfiguration))
      assignAnimatedValue(translateY, withTiming(0))
    } else {
      assignAnimatedValue(heightY, withTiming(0, timingConfiguration))
      assignAnimatedValue(translateY, withTiming(-messageHeight - 100, timingConfiguration))
    }
  }, [showingDonationBanner, translateY, heightY, messageHeight, lessThan.md])

  const onLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    if (lessThan.md) return

    // Update alert position when window size is updated
    if (!messageHeight && layout.height > 0) {
      setMessageHeight(() => layout.height)
      assignAnimatedValue(heightY, withTiming(-100 - messageHeight))
    }
    if (messageHeight !== layout.height && layout.height > 0) {
      setMessageHeight(() => layout.height)
      if (showingDonationBanner) {
        assignAnimatedValue(heightY, withTiming(messageHeight))
      } else {
        assignAnimatedValue(heightY, withTiming(0, timingConfiguration))
        assignAnimatedValue(translateY, withTiming(-100 - messageHeight, timingConfiguration))
      }
    }
  }
  return { animatedY, translateY, animatedHeightY, layoutRef, onLayout }
}
